/**
 * Centralized Status Mapping Utility
 *
 * Eliminates status mapping duplication across components.
 * Provides consistent status display with colors and labels.
 */

import { designTokens } from '../styles/design-tokens';

// NOTE: "MESSAGE READY" and "WRITING MESSAGE" are NOT top-level statuses — they
// are TRANSITIONAL SUB_STATUSES of ENRICHED (moved from OUTREACHING; see
// getTransitionalStatusLabel). They normalize to 'enriched', so they count as
// ENRICHED in every metric / funnel while their friendly "…" label shows in
// status pills. OUTREACHED is now a top-level STATUS (substatus: "awaiting").
export type StatusType = 'new_lead' | 'enriching' | 'enriched' | 'outreaching' | 'outreached' | 'engaged' | 'proposal' | 'converted' | 'discarded';

/**
 * Per-status substatus vocabulary (database contract — leads now arrive with
 * status + substatus, e.g. "OUTREACHING · not started"). A label ending in
 * "…" marks a TRANSITIONAL substatus: an intermediate processing state the
 * SYSTEM writes (writing message…, message ready…). Transitional entries are
 * NEVER selectable by users — the ISR stepper renders them disabled/informative.
 */
export interface LeadSubstatusDef {
  key: string;
  label: string;
}

export const LEAD_SUBSTATUS_BY_STATUS: Record<StatusType, LeadSubstatusDef[]> = {
  new_lead: [
    { key: 'ready', label: 'Ready' },
  ],
  enriching: [],
  enriched: [
    { key: 'ready', label: 'Ready' },
    // Transitional — system only (moved from OUTREACHING to ENRICHED).
    { key: 'writting_message', label: 'Writing message…' },
    // `message_ready` and `ready_to_outreach` are the SAME user-visible state —
    // both keys map to "Ready to Outreach" so either DB value renders one label.
    { key: 'message_ready', label: 'Ready to Outreach' },
    { key: 'ready_to_outreach', label: 'Ready to Outreach' },
  ],
  outreaching: [
    { key: 'not_started', label: 'Not started' },
    { key: 'icebreaker', label: 'Icebreaker' },
    { key: 'follow_up', label: 'Follow-up' },
    { key: 'last_message', label: 'Last message' },
  ],
  // OUTREACHED is now a top-level STATUS with exactly one substatus.
  outreached: [
    { key: 'awaiting', label: 'Awaiting' },
  ],
  engaged: [
    { key: 'reply_received', label: 'Reply received' },
    { key: 'in_conversation', label: 'In conversation' },
    { key: 'meeting_requested', label: 'Meeting requested' },
    { key: 'meeting_scheduled', label: 'Meeting scheduled' },
    { key: 'discovery_completed', label: 'Discovery completed' },
    { key: 'qualified', label: 'Qualified' },
    { key: 'objection', label: 'Objection raised' },
  ],
  proposal: [
    { key: 'proposal_prepared', label: 'Proposal prepared' },
    { key: 'review_scheduled', label: 'Review scheduled' },
    { key: 'objections_resolved', label: 'Objections resolved' },
    { key: 'decision_process_identified', label: 'Decision process identified' },
    { key: 'next_action_scheduled', label: 'Next action scheduled' },
  ],
  converted: [
    { key: 'closed_won', label: 'Closed-won' },
    { key: 'onboarding', label: 'Onboarding' },
    { key: 'active', label: 'Active' },
    { key: 'upsell', label: 'Upsell opportunity' },
    { key: 'support', label: 'Support needed' },
    { key: 'at_risk', label: 'At risk' },
  ],
  discarded: [
    { key: 'not_interested', label: 'Not interested' },
    { key: 'wrong_person', label: 'Wrong person' },
    { key: 'out_of_icp', label: 'Out of ICP' },
    { key: 'bad_timing', label: 'Bad timing' },
    { key: 'no_response', label: 'No response' },
    { key: 'bounced', label: 'Bounced' },
  ],
};

/** True when the substatus label ends with "…" — a system-owned transitional
 *  processing state (never user-selectable; shown for information only). */
export const isTransitionalSubstatus = (substatus: unknown): boolean => {
  if (typeof substatus !== 'string') return false;
  const entry = findLeadSubstatus(substatus);
  return Boolean(entry && entry.label.endsWith('…'));
};

/** Resolve a substatus key (any casing/separator) to its definition. */
export const findLeadSubstatus = (substatus: unknown): LeadSubstatusDef | null => {
  if (typeof substatus !== 'string' || !substatus.trim()) return null;
  const normalized = substatus.trim().toLowerCase().replace(/[\s-]+/g, '_');
  for (const defs of Object.values(LEAD_SUBSTATUS_BY_STATUS)) {
    const hit = defs.find((def) => def.key === normalized);
    if (hit) return hit;
  }
  return null;
};

/** Display label for a substatus (handles transitional "…" labels and raw
 *  values arriving from the DB that predate the vocabulary). */
export const getLeadSubstatusLabel = (substatus: unknown): string | null => {
  if (typeof substatus !== 'string' || !substatus.trim()) return null;
  const normalized = substatus.trim().toLowerCase().replace(/[\s-]+/g, '_');
  // Retired from ENGAGED: suppress stale persisted values so every surface
  // falls back to the current entry substatus instead of reviving the label.
  if (normalized === 'asset_sent') return null;
  return findLeadSubstatus(substatus)?.label
    ?? substatus.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const SALES_STATUSES_WITH_ENTRY_SUBSTATUS = new Set<StatusType>([
  'enriched',
  'outreaching',
  'outreached',
  'engaged',
  'proposal',
  'converted',
  'discarded',
]);

/**
 * Resolve the substatus to render in a status pill. When a Sales Intelligence
 * stage has no stored substatus yet, mirror the ISR stepper by displaying that
 * stage's first option as its entry state. Pre-sales stages remain unchanged.
 */
export const getLeadSubstatusDisplay = (status: unknown, substatus: unknown): string | null => {
  const explicitLabel = getLeadSubstatusLabel(substatus);
  if (explicitLabel) return explicitLabel;

  const statusKey = getNormalizedStatusKey(status);
  if (!SALES_STATUSES_WITH_ENTRY_SUBSTATUS.has(statusKey)) return null;

  return LEAD_SUBSTATUS_BY_STATUS[statusKey][0]?.label ?? null;
};

export interface StatusConfig {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  tailwindColor: string; // Tailwind class for dynamic colors
  tailwindBg: string; // Tailwind class for backgrounds
  tailwindBorder: string; // Tailwind class for borders
  dotColor: string; // For status filter dots
  icon?: string;
  isPro?: boolean; // Flag for PRO features
}

const extractStatusValue = (status: unknown): string | undefined => {
  if (typeof status === 'string') {
    return status;
  }

  if (status === null || status === undefined) {
    return undefined;
  }

  if (Array.isArray(status)) {
    for (const item of status) {
      const nestedStatus = extractStatusValue(item);
      if (nestedStatus?.trim()) {
        return nestedStatus;
      }
    }

    return undefined;
  }

  if (typeof status === 'object') {
    const record = status as Record<string, unknown>;
    const candidates = [
      record.value,
      record.status,
      record.label,
      record.name,
      record.current_status,
      record.workflow_status,
      record.lead_status,
    ];

    for (const candidate of candidates) {
      const nestedStatus = extractStatusValue(candidate);
      if (nestedStatus?.trim()) {
        return nestedStatus;
      }
    }
  }

  return String(status);
};

const cleanStatus = (status: unknown): string => {
  const resolvedStatus = extractStatusValue(status);

  if (!resolvedStatus || resolvedStatus.trim() === '') {
    return '';
  }

  return resolvedStatus
    .replace(/[._-]+/g, ' ')
    .toUpperCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[.…\s]+$/, '');
};

const formatUnknownStatusLabel = (status: string): string =>
  status.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

export const getNormalizedStatusKey = (status: unknown): StatusType => {
  const statusUpper = cleanStatus(status);

  if (!statusUpper) {
    return 'new_lead';
  }

  if (statusUpper === 'NEW LEAD' || statusUpper === 'NEW' || statusUpper === 'NEW_LEAD') {
    return 'new_lead';
  }

  if (statusUpper === 'ENRICHING' || statusUpper === 'SUMMARIZING') {
    return 'enriching';
  }

  if (
    statusUpper === 'ENRICHED' ||
    statusUpper === 'PROFILED' ||
    // Transitional ENRICHED substatuses (moved from OUTREACHING — the DB writes
    // these while a message is drafted/approved). They normalize to 'enriched'
    // so they keep the ENRICHED color + funnel position; the friendly substatus
    // label (with trailing "…") resolves via getTransitionalStatusLabel().
    statusUpper === 'READY TO OUTREACH' ||
    statusUpper === 'READY_TO_OUTREACH' ||
    statusUpper === 'READY' ||
    statusUpper.includes('WRITTING MESSAGE') ||
    statusUpper.includes('WRITING MESSAGE') ||
    statusUpper.includes('MESSAGE READY') ||
    statusUpper === 'QUEUE' ||
    statusUpper === 'QUEUED' ||
    statusUpper.includes('QUEUED MESSAGE') ||
    statusUpper.includes('QUEUE MESSAGE')
  ) {
    return 'enriched';
  }

  if (statusUpper === 'OUTREACHING') {
    return 'outreaching';
  }

  if (statusUpper === 'OUTREACHED') {
    return 'outreached';
  }

  if (statusUpper === 'ENGAGED') {
    return 'engaged';
  }

  if (statusUpper === 'PROPOSAL') {
    return 'proposal';
  }

  if (statusUpper === 'CONVERTED' || statusUpper === 'CONVERSION' || statusUpper === 'CONVERTED') {
    return 'converted';
  }

  if (statusUpper === 'DISCARDED') {
    return 'discarded';
  }

  return 'new_lead';
};

/**
 * Friendly label for a TRANSITIONAL (in-progress) substatus that sits UNDER a
 * parent status. These now live under ENRICHED (moved from OUTREACHING): the DB
 * writes them as a lead moves from enriched → writing message… → message
 * ready… → ready to outreach → OUTREACHING. The trailing "…" signals a
 * temporal processing state, not a final one. Returns null when the status
 * isn't a recognized transitional substatus, so callers fall back to the
 * umbrella status label.
 */
export const getTransitionalStatusLabel = (status: unknown): string | null => {
  const statusUpper = cleanStatus(status);
  if (!statusUpper) return null;

  if (statusUpper.includes('MESSAGE READY')) return 'Ready to Outreach';
  if (statusUpper.includes('QUEUED MESSAGE') || statusUpper.includes('QUEUE MESSAGE')) return 'Queued message…';
  if (statusUpper === 'QUEUE' || statusUpper === 'QUEUED') return 'Queued message…';
  if (statusUpper.includes('WRITTING MESSAGE') || statusUpper.includes('WRITING MESSAGE')) return 'Writing message…';
  if (statusUpper.includes('READY TO OUTREACH')) return 'Ready to Outreach';

  return null;
};

const isExplicitNewLeadStatus = (status: unknown): boolean => {
  const statusUpper = cleanStatus(status);
  return statusUpper === 'NEW LEAD' || statusUpper === 'NEW' || statusUpper === 'NEW_LEAD';
};

const isKnownStatus = (status: unknown): boolean => {
  const statusUpper = cleanStatus(status);

  if (!statusUpper) {
    return false;
  }

  return getNormalizedStatusKey(status) !== 'new_lead' || isExplicitNewLeadStatus(status);
};

/**
 * Status configuration map with WCAG-compliant colors
 * Matches lead lifecycle: New Lead → Enriching → Enriched → Outreaching → Outreached → Engaged
 */
export const STATUS_MAP: Record<StatusType, StatusConfig> = {
  new_lead: {
    label: 'NEW LEAD',
    color: '#6B7280', // Standardized grey
    bgColor: 'rgba(107, 114, 128, 0.1)',
    borderColor: 'rgba(107, 114, 128, 0.2)',
    tailwindColor: 'text-[#6B7280]',
    tailwindBg: 'bg-gray-500/10',
    tailwindBorder: 'border-gray-500/20',
    dotColor: 'bg-gray-400',
    icon: '🆕',
  },
  enriching: {
    label: 'ENRICHING',
    color: '#FBBF24', // Yellow (WCAG AA compliant)
    bgColor: 'rgba(251, 191, 36, 0.1)',
    borderColor: 'rgba(251, 191, 36, 0.2)',
    tailwindColor: 'text-yellow-400',
    tailwindBg: 'bg-yellow-500/10',
    tailwindBorder: 'border-yellow-500/20',
    dotColor: 'bg-yellow-400',
    icon: '⚙️',
  },
  enriched: {
    label: 'ENRICHED',
    color: '#3B82F6', // Standardized blue
    bgColor: 'rgba(59, 130, 246, 0.1)',
    borderColor: 'rgba(59, 130, 246, 0.2)',
    tailwindColor: 'text-[#3B82F6]',
    tailwindBg: 'bg-blue-500/10',
    tailwindBorder: 'border-blue-500/20',
    dotColor: 'bg-blue-400',
    icon: '✨',
  },
  outreaching: {
    label: 'OUTREACHING',
    color: '#F97316', // Amber-orange to separate active outreach from enriched
    bgColor: 'rgba(249, 115, 22, 0.1)',
    borderColor: 'rgba(249, 115, 22, 0.22)',
    tailwindColor: 'text-orange-400',
    tailwindBg: 'bg-orange-500/10',
    tailwindBorder: 'border-orange-500/20',
    dotColor: 'bg-orange-400',
    icon: '📤',
  },
  outreached: {
    label: 'OUTREACHED',
    color: '#A855F7', // Standardized purple
    bgColor: 'rgba(168, 85, 247, 0.1)',
    borderColor: 'rgba(168, 85, 247, 0.2)',
    tailwindColor: 'text-[#A855F7]',
    tailwindBg: 'bg-purple-500/10',
    tailwindBorder: 'border-purple-500/20',
    dotColor: 'bg-purple-400',
    icon: '✅',
    isPro: true,
  },
  engaged: {
    label: 'ENGAGED',
    color: '#EC4899', // Standardized pink
    bgColor: 'rgba(236, 72, 153, 0.1)',
    borderColor: 'rgba(236, 72, 153, 0.2)',
    tailwindColor: 'text-[#EC4899]',
    tailwindBg: 'bg-pink-500/10',
    tailwindBorder: 'border-pink-500/20',
    dotColor: 'bg-pink-400',
    icon: '🤝',
    isPro: true,
  },
  proposal: {
    label: 'PROPOSAL',
    color: '#06B6D4',
    bgColor: 'rgba(6, 182, 212, 0.1)',
    borderColor: 'rgba(6, 182, 212, 0.22)',
    tailwindColor: 'text-cyan-400',
    tailwindBg: 'bg-cyan-500/10',
    tailwindBorder: 'border-cyan-500/20',
    dotColor: 'bg-cyan-400',
    icon: '📄',
    isPro: true,
  },
  converted: {
    label: 'CONVERTED',
    color: '#10b981', // Green/Emerald (for conversion success)
    bgColor: 'rgba(16, 185, 129, 0.1)',
    borderColor: 'rgba(16, 185, 129, 0.2)',
    tailwindColor: 'text-emerald-500',
    tailwindBg: 'bg-emerald-500/10',
    tailwindBorder: 'border-emerald-500/20',
    dotColor: 'bg-emerald-500',
    icon: '🎯',
    isPro: true,
  },
  discarded: {
    label: 'DISCARDED',
    color: '#9CA3AF', // Neutral grey — discarded leads are out of the funnel,
    // not an error state, so grey reads as "set aside" rather than alarming red.
    bgColor: 'rgba(156, 163, 175, 0.1)',
    borderColor: 'rgba(156, 163, 175, 0.2)',
    tailwindColor: 'text-gray-400',
    tailwindBg: 'bg-gray-500/10',
    tailwindBorder: 'border-gray-500/20',
    dotColor: 'bg-gray-400',
    icon: '🚫',
  },
};

/**
 * Get status configuration by status key
 * Handles various status aliases and formats
 */
export const getStatusConfig = (status: unknown): StatusConfig => {
  const cleanedStatus = cleanStatus(status);
  const normalizedStatus = getNormalizedStatusKey(status);
  const config = STATUS_MAP[normalizedStatus];

  if (normalizedStatus === 'new_lead' && cleanedStatus && !isExplicitNewLeadStatus(status)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️ Unrecognized lead status:', status, '(cleaned:', cleanedStatus + ')');
    }

    return {
      ...config,
      label: formatUnknownStatusLabel(cleanedStatus),
    };
  }

  return config;
};

/**
 * Get status label (uppercase, formatted)
 */
export const getStatusLabel = (status: unknown): string => {
  return getStatusConfig(status).label;
};

/**
 * True once a lead has completed enrichment (enriched → outreaching →
 * outreached → engaged → proposal → converted). The ISR workspace / "Human" surfaces
 * only make sense from this point — a NEW LEAD or ENRICHING lead has no
 * briefing, sequence, or activity to work with yet. Discarded leads also
 * passed enrichment, but they're out of the funnel and stay excluded.
 */
export const isLeadEnrichedForISR = (status: unknown): boolean => {
  const key = getNormalizedStatusKey(status);
  return (
    key === 'enriched' ||
    key === 'outreaching' ||
    key === 'outreached' ||
    key === 'engaged' ||
    key === 'proposal' ||
    key === 'converted'
  );
};

/**
 * Get normalized status lifecycle order for comparisons
 */
export const getStatusRank = (status: unknown): number => {
  switch (getNormalizedStatusKey(status)) {
    case 'new_lead':
      return 0;
    case 'enriching':
      return 1;
    case 'enriched':
      return 2;
    case 'outreaching':
      return 3;
    case 'outreached':
      return 4;
    case 'engaged':
      return 5;
    case 'proposal':
      return 6;
    case 'converted':
      return 7;
    case 'discarded':
      return 8;
    default:
      return 0;
  }
};

/**
 * Prefer the most advanced known status when two sources disagree
 */
export const getPreferredStatus = (
  primaryStatus: unknown,
  fallbackStatus: unknown
): string | undefined => {
  const resolvedPrimaryStatus = extractStatusValue(primaryStatus);
  const resolvedFallbackStatus = extractStatusValue(fallbackStatus);

  if (!resolvedPrimaryStatus?.trim()) return resolvedFallbackStatus;
  if (!resolvedFallbackStatus?.trim()) return resolvedPrimaryStatus;

  const primaryKnownStatus = isKnownStatus(resolvedPrimaryStatus);
  const fallbackKnownStatus = isKnownStatus(resolvedFallbackStatus);

  if (primaryKnownStatus && !fallbackKnownStatus) {
    return getNormalizedStatusKey(resolvedPrimaryStatus) === 'new_lead'
      ? resolvedFallbackStatus
      : resolvedPrimaryStatus;
  }

  if (!primaryKnownStatus && fallbackKnownStatus) {
    return getNormalizedStatusKey(resolvedFallbackStatus) === 'new_lead'
      ? resolvedPrimaryStatus
      : resolvedFallbackStatus;
  }

  if (!primaryKnownStatus && !fallbackKnownStatus) {
    return resolvedPrimaryStatus;
  }

  return getStatusRank(resolvedPrimaryStatus) >= getStatusRank(resolvedFallbackStatus)
    ? resolvedPrimaryStatus
    : resolvedFallbackStatus;
};

/**
 * Get status color (text color)
 */
export const getStatusColor = (status: unknown): string => {
  return getStatusConfig(status).color;
};

/**
 * Get status background color
 */
export const getStatusBgColor = (status: unknown): string => {
  return getStatusConfig(status).bgColor;
};

/**
 * Get status border color
 */
export const getStatusBorderColor = (status: unknown): string => {
  return getStatusConfig(status).borderColor;
};

/**
 * Get status icon emoji
 */
export const getStatusIcon = (status: unknown): string | undefined => {
  return getStatusConfig(status).icon;
};

/**
 * Get all available status types
 */
export const getAllStatuses = (): StatusType[] => {
  return Object.keys(STATUS_MAP) as StatusType[];
};

/**
 * Check if a status is valid
 */
export const isValidStatus = (status: unknown): boolean => {
  return isKnownStatus(status);
};

/**
 * Get status badge className string
 */
export const getStatusBadgeClasses = (status: unknown): string => {
  const config = getStatusConfig(status);
  return `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all`;
};

/**
 * Get status badge inline styles
 */
export const getStatusBadgeStyles = (status: unknown): React.CSSProperties => {
  const config = getStatusConfig(status);
  return {
    color: config.color,
    backgroundColor: config.bgColor,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: config.borderColor,
  };
};

/**
 * Get status filter options for dropdown
 */
export const getStatusFilterOptions = () => {
  return [
    { value: 'all', label: 'All Status', color: 'text-white', dotColor: 'bg-white/50', hexColor: '#ffffff' },
    ...Object.entries(STATUS_MAP)
      .map(([key, config]) => ({
        value: config.label, // Use label as value for backward compatibility
        label: config.label,
        color: config.tailwindColor,
        dotColor: config.dotColor,
        hexColor: config.color,
      }))
  ];
};

/**
 * Get filter category from status label
 * Used for status filtering logic
 * Returns uppercase labels to match dropdown filter values
 */
export const getFilterCategory = (status: unknown): string => {
  return getStatusConfig(status).label;
};

export default {
  STATUS_MAP,
  getStatusConfig,
  getNormalizedStatusKey,
  getTransitionalStatusLabel,
  getStatusLabel,
  getStatusRank,
  getPreferredStatus,
  getStatusColor,
  getStatusBgColor,
  getStatusBorderColor,
  getStatusIcon,
  getAllStatuses,
  isValidStatus,
  getStatusBadgeClasses,
  getStatusBadgeStyles,
  getStatusFilterOptions,
  getFilterCategory,
};
